/**
 * feat-242 — every user-visible string the Map adds, in one module.
 *
 * WHY ONE MODULE. Two rules meet here and both are census rules:
 *
 *  * AC19 — every noun naming a Track or a Lesson renders through `QUIZ_TERMS`,
 *    never a literal. A literal-text grep returns a **true zero** for on-screen
 *    copy in this repo, so a literal here is invisible to exactly the instrument
 *    that would catch it. Keeping the copy in one place makes "did every noun go
 *    through the constant?" a question you can answer by reading one file.
 *
 *  * AC13 — none of the phone's learner-status vocabulary may appear in rendered
 *    output. `Tier`, `Editor`, `Map`, `On` and `Hidden` are NOT routed through
 *    `QUIZ_TERMS` because none of them names a Track or a Lesson (mockup S7 note
 *    1) — but they are still here, so a later rename is one edit rather than a
 *    sweep.
 *
 * D11, restated because it is the rule every string below was written against:
 *
 *   > The Map may make statements about the CURRICULUM. It may never make a
 *   > statement about a LEARNER.
 *
 * So: no `locked` / `unlocked` / `available` / `in progress` / `completed` /
 * `unlocks when`, and — going deliberately stricter than AC13's literal list,
 * per OPEN-QUESTIONS Q5 — **no member of the lock/unlock word family in any
 * inflection at all**. That includes the sentence in `spec.md` §3's *allowed*
 * column ("…can ever unlock"). It appears there as an illustration of the
 * allowed CATEGORY, not as approved copy. Do not restore it.
 *
 * Each hazard states the structural fact and its consequence in vocabulary the
 * spec itself uses elsewhere: "nobody can open this" (§1 and AC7, verbatim),
 * "has no On Lessons", "that requirement is ignored", "open to the whole crew
 * right now" (§4 H3, verbatim), "require each other in a loop" (§3, verbatim).
 */

import { QUIZ_TERMS, termCount } from '../../../quizTerminology';

/** Not routed through `QUIZ_TERMS` — none of these names a Track or a Lesson. */
export const MAP_LABELS = {
  editor: 'Editor',
  map: 'Map',
  tier: 'Tier',
  on: 'On',
  hidden: 'Hidden',
} as const;

/** The glyph per hazard. The four marks are told apart by GLYPH, terminator
 *  shape and placement — never by hue, because `attention-surface #FFF4D6` and
 *  `danger-surface #FEF2F2` are L 0.908 vs 0.910 and a design leaning on fill
 *  alone collapses in greyscale and for a red/green-deficient reader. */
export const HAZARD_GLYPH = {
  h1: '⊘',
  h2: '⊖',
  h3: '⇢',
  h4: '↻',
} as const;

const track = () => QUIZ_TERMS.track.singular;
const tracks = () => QUIZ_TERMS.track.plural;
const lesson = () => QUIZ_TERMS.lesson.singular;
const lessons = () => QUIZ_TERMS.lesson.plural;

export const MAP_COPY = {
  /** The view bar's one-line description. Says the read-only rule out loud
   *  rather than relying on the absence of controls to imply it. */
  viewBarBlurb: () =>
    'Everything this tenant has authored, and what requires what. Nothing here changes anything.',

  tierHeading: (n: number) => `${MAP_LABELS.tier} ${n}`,
  tierSubtitle: (n: number) =>
    n === 0 ? 'Requires nothing' : `Requires something in ${MAP_LABELS.tier} ${n - 1}`,

  // --- H1 — the only mark that lands on a Lesson rather than a Track --------
  unreachableLesson: () => 'nobody can open this',

  // --- H2 — a dead end behind it -------------------------------------------
  /** One hop. `path` has a single element. */
  deadEndDirect: (emptyTitle: string) =>
    `Requires ${emptyTitle}, which has no ${lessons()} that are ${MAP_LABELS.on}. ` +
    `Nothing behind it can ever open for the crew.`,
  /** Two or more hops — AC10. A one-hop implementation renders the wrong
   *  sentence here while still being "marked", which is why the chain is
   *  carried rather than just a boolean. */
  deadEndTransitive: (firstTitle: string, emptyTitle: string) =>
    `Requires ${firstTitle}, which requires ${emptyTitle} — and that has no ` +
    `${lessons()} that are ${MAP_LABELS.on}.`,

  /** H2's CAUSE, in the empty Track's own body, so cause and effect are on
   *  screen together. Used ONLY when something actually requires this Track —
   *  see `emptyTrackNothingYet` for why that distinction is load-bearing. */
  emptyTrackHeadline: () => `No ${lessons()} that are ${MAP_LABELS.on}`,
  // `termCount` pluralises the NOUN, so the VERB has to agree separately — at
  // n = 1 this read "1 Track require this one." A helper that returns "1 Track"
  // does not make the sentence around it singular, and there is no lint that
  // notices; the only instrument is reading the rendered string at n = 1.
  emptyTrackRequiredBy: (n: number) =>
    `${termCount(n, 'track')} ${n === 1 ? 'requires' : 'require'} this one.`,
  emptyTrackAllHidden: (n: number) =>
    `${termCount(n, 'lesson')}, none of them ${MAP_LABELS.on}.`,

  /**
   * The SAME structural fact, stated for a Track that **nothing requires**.
   *
   * H2's hazard is *a dead end behind it*. With nothing behind it there is no
   * dead end and there is no hazard — there is a Track the author has not filled
   * in yet, which is the most ordinary state a curriculum passes through. A
   * brand-new tenant with two empty Tracks was getting two marks in the family
   * that means "you authored this, it reaches nobody, and you can fix it", **for
   * having authored nothing yet.**
   *
   * That is the "painting a legitimate state as an error" failure this whole
   * slice was built to avoid — the same one the shipped `LessonLibraryPane`
   * ruling protects against for unfiled Lessons ("a draft, a seasonal Lesson
   * pulled for the winter"). Sharpen the predicate; do not widen the paint.
   *
   * So this copy is FACTUAL, not accusatory, and it renders in the neutral
   * treatment. Nothing is being reported.
   */
  emptyTrackNothingYet: () => `Nothing in this ${track()} yet.`,

  // --- H3 — a requirement nothing acts on ----------------------------------
  /** (a) the required Track is Hidden. `actedOn` is what IS gated on, which is
   *  the half that tells the author the rest of their intent survived. */
  ignoredHiddenRequired: (ignoredTitles: string, actedOnTitles: string | null) =>
    `Also requires ${ignoredTitles}, which is ${MAP_LABELS.hidden}. A ${MAP_LABELS.hidden} ` +
    `${track()} is not a route to content, so that requirement is ignored` +
    (actedOnTitles ? ` — only ${actedOnTitles} is acted on.` : '.'),
  /** (b) stale rows on an `open`-mode Track. The consequence is the opposite of
   *  what the author ticked, so it is stated rather than implied. */
  ignoredOpenMode: (ignoredTitles: string) =>
    `Set to Anyone, but it still lists ${ignoredTitles} as required. That requirement ` +
    `is ignored — this ${track()} is open to the whole crew right now.`,

  // --- H4 — a loop ----------------------------------------------------------
  loopGroupLabel: () => 'These require each other',
  loopGroupNoTier: () => `No ${MAP_LABELS.tier.toLowerCase()}`,
  loopGroupBody: (n: number) =>
    `${termCount(n, 'track')} require each other in a loop. None of them can sit in a ` +
    `${MAP_LABELS.tier.toLowerCase()}, and none of the ${n} can be reached through the others.`,

  // --- Hidden Track footnote ------------------------------------------------
  hiddenTrackNote: () =>
    `This ${track()} is ${MAP_LABELS.hidden}, so nothing in it is a route to content.`,

  // --- The "In no Track" region --------------------------------------------
  /** Q3: the visible heading is `In no {Track}`, matching the shipped filter
   *  chip and count line one pane to the right. "Unfiled" survives only as the
   *  `data-testid`, so the spec's word still addresses the thing in tests.
   *  Introducing a second user-visible noun for one population is the drift the
   *  terminology rule exists to prevent. */
  unfiledHeading: () => `In no ${track()}`,
  unfiledCount: (n: number) => termCount(n, 'lesson'),
  unfiledBlurb: () =>
    `A ${lesson()} in no ${track()} is fine — a draft, a seasonal ${lesson()} pulled for ` +
    `the winter. Only the ones that are ${MAP_LABELS.on} and that no visible ${track()} ` +
    `carries are marked.`,

  // --- The affirmative state ------------------------------------------------
  /** Q7 — modelled on the shipped "In no Track" empty state, which is
   *  deliberately affirmative rather than a negation ("which would read as a
   *  failed query"). WATCH THIS ONE: it makes a claim about reachability, and
   *  reachability is one clause away from a claim about a learner. It survives
   *  D11 because it holds for every learner simultaneously and names no status —
   *  but it is the closest string on this canvas to the boundary. Do not
   *  paraphrase it toward status vocabulary. */
  noHazardsHeadline: () => 'Nothing on this canvas needs your attention.',
  noHazardsBody: () =>
    `Every ${lesson()} that is ${MAP_LABELS.on} is in a ${track()} the crew can reach.`,

  /** D3 stated where a single-active-track tenant will read it — the reason
   *  Ladle & Leaf sees no mark is a fact about their shape, not an omission. */
  singleTrackNote: () =>
    `With one visible ${track()}, the crew's app shows the flat ${lesson()} list, so a ` +
    `${lesson()} in no ${track()} is still in front of them.`,

  // --- Empty / error --------------------------------------------------------
  emptyHeadline: (n: number) =>
    n === 1
      ? `There's one ${track()} and nothing in it yet.`
      : `There are no ${tracks()} yet.`,
  emptyBody: () =>
    `The ${MAP_LABELS.map} fills in as you file ${lessons()}. Switch to ${MAP_LABELS.editor} to start.`,

  // --- Titles / affordances -------------------------------------------------
  trackNodeTitle: (title: string) => `Open ${title} in ${MAP_LABELS.editor}`,
  lessonChipTitle: (title: string) => `Open ${title} in ${lessons()}`,
  sharedLessonTitle: (n: number) => `In ${termCount(n, 'track')}`,
} as const;

/**
 * AC13's forbidden vocabulary, exported so the census asserts against the SAME
 * list this module was written against rather than a second copy that can drift.
 *
 * Written as patterns, not as six exact strings: `/lock|unlock/i` catches every
 * inflection, which is what Q5 bought by keeping the word family out of the copy
 * entirely. Six exact strings would pass on "unlocking" and on "will unlock".
 */
export const FORBIDDEN_STATUS_PATTERNS: readonly RegExp[] = [
  /lock/i, // covers lock, locked, unlock, unlocked, unlocks, unlocking, "unlocks when"
  /\bavailable\b/i,
  /\bin progress\b/i,
  /\bcompleted\b/i,
  /\bcomplete\b/i,
];
